import { Checkbox, InputNumber, Popconfirm, Select } from 'antd';
import { useEffect, useRef, useState } from 'react';

import type { AwardLevelOption, ScoreFieldOption } from './types';

// Table cells of the score view: a Participant's score on one Score field,
// and their Award level. A click inside never reaches the row (which opens
// the drawer).
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

// Saves on Enter or when focus leaves; Enter also moves to the same field on
// the next row, so a column of scores can be typed in one go. Escape undoes.
// The field's min–max bounds the input; the server checks them again.
export function ScoreCell({
  participantId,
  field,
  value,
  disabled,
  onSave,
  onNext,
}: {
  participantId: number;
  field: ScoreFieldOption;
  value: number | null;
  disabled: boolean;
  onSave: (score: number | null) => Promise<boolean>;
  onNext: () => void;
}) {
  const [draft, setDraft] = useState<number | null>(value);
  const [saving, setSaving] = useState(false);
  // What the server holds: Enter saves, then the blur that follows must not save again.
  const saved = useRef<number | null>(value);

  useEffect(() => {
    saved.current = value;
    setDraft(value);
  }, [value]);

  const commit = async () => {
    if (draft === saved.current) return true;
    setSaving(true);
    const ok = await onSave(draft);
    setSaving(false);
    if (ok) saved.current = draft;
    else setDraft(saved.current);
    return ok;
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only keeps the click off the row
    <div onClick={stop} onKeyDown={stop}>
      <InputNumber
        size="small"
        variant="filled"
        style={{ width: '100%' }}
        min={field.min_score}
        max={field.max_score}
        placeholder={`${field.min_score}–${field.max_score}`}
        data-score={`${field.id}:${participantId}`}
        disabled={disabled || saving}
        value={draft}
        onChange={(v) => setDraft(v ?? null)}
        onBlur={commit}
        onKeyDown={async (e) => {
          e.stopPropagation();
          if (e.key === 'Escape') {
            setDraft(saved.current);
            e.currentTarget.blur();
          } else if (e.key === 'Enter') {
            if (await commit()) onNext();
          }
        }}
      />
    </div>
  );
}

export function AwardCell({
  value,
  awardLevels,
  disabled,
  onSave,
}: {
  value: number | null;
  awardLevels: AwardLevelOption[];
  disabled: boolean;
  onSave: (awardLevelId: number | null) => Promise<boolean>;
}) {
  const [saving, setSaving] = useState(false);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only keeps the click off the row
    <div onClick={stop} onKeyDown={stop}>
      <Select
        size="small"
        variant="filled"
        style={{ width: '100%' }}
        placeholder="Шагнал"
        allowClear
        popupMatchSelectWidth={false}
        disabled={disabled || saving}
        loading={saving}
        value={value ?? undefined}
        options={awardLevels.map((a) => ({ value: a.id, label: a.name }))}
        onChange={async (v) => {
          setSaving(true);
          await onSave(v ?? null);
          setSaving(false);
        }}
      />
    </div>
  );
}

// Absent (CONTEXT.md): marking a Participant who already has scores asks
// first, since their scores and award are removed.
export function AbsentCell({
  value,
  hasScores,
  disabled,
  onSave,
}: {
  value: boolean;
  hasScores: boolean;
  disabled: boolean;
  onSave: (absent: boolean) => Promise<boolean>;
}) {
  const [saving, setSaving] = useState(false);
  const confirm = !value && hasScores;
  const save = async (absent: boolean) => {
    setSaving(true);
    await onSave(absent);
    setSaving(false);
  };
  // A plain Checkbox unless a confirm is needed: a disabled Popconfirm still
  // wraps its child and swallows the click.
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only keeps the click off the row
    <div onClick={stop} onKeyDown={stop}>
      {confirm ? (
        <Popconfirm
          title="Ирээгүй гэж тэмдэглэх үү?"
          description="Оруулсан оноо, шагнал устгагдана."
          okText="Тэмдэглэх"
          cancelText="Болих"
          onConfirm={() => save(true)}
        >
          <Checkbox checked={false} disabled={disabled || saving} />
        </Popconfirm>
      ) : (
        <Checkbox
          checked={value}
          disabled={disabled || saving}
          onChange={(e) => save(e.target.checked)}
        />
      )}
    </div>
  );
}
